/**
 * Citron confetti (sections/citron-promo-banner.liquid).
 *
 * Gold paper confetti on a <canvas> that covers the banner and spills into
 * the section below. Vanilla, no dependencies, loaded with `defer` and only
 * when the section has confetti switched on.
 *
 * - Starts when the banner is 35% on screen (IntersectionObserver).
 * - "burst": one burst that settles and fades out over the set duration,
 *   then stops for good and releases the canvas memory.
 *   "loop": a gentle, sparser drift that runs while visible.
 * - Pauses whenever the confetti area is off screen or the tab is hidden.
 * - Does nothing at all with prefers-reduced-motion (the CSS hides it too).
 * - The canvas takes no pointer events, so taps always reach what is below.
 */
(() => {
  if (customElements.get('citron-confetti')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DEFAULT_COLORS = ['#2B3BFF', '#6F7DFF', '#E8ECFF', '#00008B'];
  const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
  const TAU = Math.PI * 2;
  const FADE_MS = 900;

  const rand = (min, max) => min + Math.random() * (max - min);
  const pick = (list) => list[Math.floor(Math.random() * list.length)];

  class CitronConfetti extends HTMLElement {
    connectedCallback() {
      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        this.hidden = true;
        return;
      }

      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas && this.canvas.getContext('2d');
      if (!this.ctx) return;

      const colors = (this.dataset.colors || '')
        .split(',')
        .map((c) => c.trim())
        .filter((c) => HEX.test(c));
      this.colors = colors.length ? colors : DEFAULT_COLORS;
      this.density = Math.min(Math.max(parseFloat(this.dataset.density) || 1, 0.1), 3);
      this.duration = Math.max(parseFloat(this.dataset.duration) || 3.5, 1) * 1000;
      this.loop = this.dataset.mode === 'loop';

      this.particles = [];
      this.elapsed = 0;
      this.started = false;
      this.finished = false;
      this.onScreen = false;
      this.frame = this.frame.bind(this);

      this.onVisibility = () => this.sync();
      document.addEventListener('visibilitychange', this.onVisibility);

      // Start trigger: the banner itself, not the taller confetti area.
      const target = this.parentElement.querySelector('[data-ctr-confetti-target]') || this.parentElement;
      this.startIO = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.startIO.disconnect();
            this.start();
          }
        },
        { threshold: 0.35 }
      );
      this.startIO.observe(target);

      // Pause trigger: any part of the confetti area visible counts as on screen.
      this.viewIO = new IntersectionObserver(([entry]) => {
        this.onScreen = entry.isIntersecting;
        this.sync();
      });
      this.viewIO.observe(this);
    }

    disconnectedCallback() {
      this.teardown();
    }

    start() {
      if (this.started) return;
      this.started = true;

      this.resize();
      this.ro = new ResizeObserver(() => this.resize());
      this.ro.observe(this);

      const base = this.width < 750 ? 60 : 120;
      this.target = Math.round(base * this.density * (this.loop ? 0.45 : 1));

      if (!this.loop) {
        // Burst: everything launches in the first ~600ms, staggered.
        for (let i = 0; i < this.target; i++) this.particles.push(this.make(true, rand(0, 600)));
      }

      this.sync();
    }

    resize() {
      const rect = this.getBoundingClientRect();
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.width = rect.width;
      this.height = rect.height;
      // The banner is the top part of the canvas; the rest is the spill.
      const banner = this.parentElement.querySelector('[data-ctr-confetti-target]');
      this.bannerHeight = banner ? banner.getBoundingClientRect().height : rect.height * 0.4;

      this.canvas.width = Math.round(this.width * this.dpr);
      this.canvas.height = Math.round(this.height * this.dpr);
    }

    /**
     * @param {boolean} burst - Pop upwards from inside the banner, or drift in from above it.
     * @param {number} delay - Milliseconds before this piece starts moving.
     */
    make(burst, delay = 0) {
      const shape = pick(['rect', 'rect', 'circle', 'strip']);
      const size = rand(0.8, 1.25);

      return {
        shape,
        color: pick(this.colors),
        w: (shape === 'strip' ? 2.2 : rand(6, 10)) * size,
        h: (shape === 'strip' ? rand(10, 15) : rand(3.5, 6)) * size,
        r: rand(2, 3.2) * size,
        x: rand(0, this.width),
        y: burst ? rand(-0.1, 0.55) * this.bannerHeight : rand(-40, -10),
        vx: burst ? rand(-90, 90) : rand(-20, 20),
        vy: burst ? rand(-260, -60) : rand(20, 60),
        rot: rand(0, TAU),
        vr: rand(-4, 4),
        flip: rand(0, TAU),
        vflip: rand(4, 9),
        sway: rand(0, TAU),
        vsway: rand(1.2, 2.6),
        swayAmp: rand(12, 34),
        delay,
      };
    }

    /** Run the frame loop only while started, unfinished, on screen and the tab is visible. */
    sync() {
      const shouldRun = this.started && !this.finished && this.onScreen && !document.hidden;

      if (shouldRun && !this.raf) {
        this.last = null;
        this.raf = requestAnimationFrame(this.frame);
      } else if (!shouldRun && this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    }

    frame(now) {
      this.raf = null;

      // Clamp so a long pause never teleports the confetti.
      const dtMs = this.last == null ? 16 : Math.min(now - this.last, 50);
      this.last = now;
      this.elapsed += dtMs;
      const dt = dtMs / 1000;

      const gravity = this.loop ? 140 : 420;
      const drag = this.loop ? 1.6 : 3.2;

      if (this.loop) {
        while (this.particles.length < this.target) this.particles.push(this.make(false, rand(0, 2500)));
      }

      const ctx = this.ctx;
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.clearRect(0, 0, this.width, this.height);

      // Burst mode fades everything out over the last FADE_MS of the duration.
      let globalAlpha = 1;
      if (!this.loop) {
        const fadeStart = this.duration - FADE_MS;
        if (this.elapsed > fadeStart) globalAlpha = Math.max(0, 1 - (this.elapsed - fadeStart) / FADE_MS);
      }

      const floor = this.height;
      const softZone = Math.min(80, floor * 0.2);

      for (let i = this.particles.length - 1; i >= 0; i--) {
        const p = this.particles[i];

        if (p.delay > 0) {
          p.delay -= dtMs;
          continue;
        }

        p.vy += gravity * dt;
        p.vy -= p.vy * drag * dt;
        p.vx -= p.vx * drag * 0.6 * dt;
        p.sway += p.vsway * dt;
        p.x += (p.vx + Math.sin(p.sway) * p.swayAmp) * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        p.flip += p.vflip * dt;

        if (p.y > floor + 20) {
          if (this.loop) this.particles[i] = this.make(false);
          else this.particles.splice(i, 1);
          continue;
        }

        // Soften pieces as they reach the bottom edge of the spill area
        // instead of letting them vanish at a hard line.
        const edgeAlpha = p.y > floor - softZone ? Math.max(0, (floor - p.y) / softZone) : 1;
        const flipScale = Math.cos(p.flip);
        // Paper catches the light as it turns: brighter when facing the viewer.
        const shimmer = 0.7 + 0.3 * Math.abs(flipScale);

        ctx.globalAlpha = globalAlpha * edgeAlpha * shimmer;
        ctx.fillStyle = p.color;
        ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.scale(1, flipScale);

        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.r, 0, TAU);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
      }

      const burstOver = !this.loop && (this.elapsed >= this.duration || this.particles.length === 0);
      if (burstOver) {
        this.finish();
        return;
      }

      this.raf = requestAnimationFrame(this.frame);
    }

    /** Burst finished: clear, stop observing, and give the canvas memory back. */
    finish() {
      this.finished = true;
      this.teardown();
      this.canvas.width = 0;
      this.canvas.height = 0;
      this.hidden = true;
    }

    teardown() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      this.startIO?.disconnect();
      this.viewIO?.disconnect();
      this.ro?.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      this.particles = [];
    }
  }

  customElements.define('citron-confetti', CitronConfetti);
})();
